import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import Icon from '../Icon'
import { colors } from '../styles/global'
import useAnnaConversation from './useAnnaConversation'
import useAnnaProjectSelection from './useAnnaProjectSelection'
import { getAnnaProjectChoices } from './annaProjectSelection'
import AnnaProjectSwitcher from './AnnaProjectSwitcher'
import useProjectData from '../../hooks/useProjectData'
import { PROJECT_DATA_ASSISTANTS } from '../../utils/InitialLoad/projectDataLoader'
import AnnaConversation from './AnnaConversation'
import AnnaWorkspaceHighlight from './AnnaWorkspaceHighlight'
import AnnaWorkspaceReveal from './AnnaWorkspaceReveal'
import { findWorkspaceObject } from './annaWorkspaceRevealTargets'
import AnnaBrowserWorkspace from './AnnaBrowserWorkspace'
import useAnnaBrowserCompletion, { browserActivityKey } from './useAnnaBrowserCompletion'
import AnnaVmWorkspace from './AnnaVmWorkspace'
import useAnnaVmJobs from './useAnnaVmJobs'
import AnnaBrowserTakeoverContext from './AnnaBrowserTakeoverContext'
import AnnaChatNavigationContext, { AnnaSidebarCloseContext } from './AnnaChatNavigationContext'
import AnnaWorkspaceNavigationContext from './AnnaWorkspaceNavigationContext'
import BrowserTakeoverPanel from '../ChatsView/ChatDV/EditorView/BrowserTakeoverPanel'
import { isAnnaNavigationPath, resolveAnnaLink } from './annaNavigation'
import { getAnnaTargetErrorMessage, resolveAnnaActivityPath } from './annaActivityNavigation'
import AmountTag from '../Feeds/FollowSwitchableTag/AmountTag'
import getAllProjectsOpenTasksAmount from '../../utils/Tasks/getAllProjectsOpenTasksAmount'
import { isAnnaWorkspacePath } from '../../functions/Assistant/annaWorkspaceContract'
import { getAssistantFromState } from '../AdminPanel/Assistants/assistantStateLookup'
import { useVoiceCall } from '../UIComponents/AssistantVoiceCallProvider'
import { getDb } from '../../utils/backends/firestore'
import { setAnnaWorkspaceContext } from '../../utils/annaWorkspaceContext'
import { getAppModelContext } from '../../utils/annaAppModelContext'
import { buildAnnaPaneContext } from './annaPaneContext'
import { setAnnaMode, setAnnaLayout, useAnnaLayout } from '../../utils/annaMode'
import useAnnaChatWidth, { MIN_CHAT_WIDTH } from './useAnnaChatWidth'
import NavigationService from '../../utils/NavigationService'
import navigateToHome from '../../utils/navigateToHome'
import URLTrigger from '../../URLSystem/URLTrigger'
import { translate, useTranslator } from '../../i18n/TranslationService'
import { sanitizeCallPageContext } from '../../functions/WhatsApp/assistantCallPageContext'
import { useWorkspaceViewportOwner } from '../../hooks/useWorkspaceViewport'
import { subscribePageVisible } from '../../utils/appResume'
import './anna.css'
import AnnaAppPaneContext from './AnnaAppPaneContext'
import { McpAppFrame, mcpAppKey } from '../ChatsView/ChatDV/EditorView/McpAppViews'
import EmbeddedAppFrame from '../UIComponents/FloatModals/IframeModal/EmbeddedAppFrame'
import { openEmbeddedAppInNewTab } from '../UIComponents/FloatModals/IframeModal/embeddedAppLocation'
import { openInEmbedWindow, registerAnnaAppPane } from './annaAppPaneBridge'

// App views kept open in the right pane; the oldest closes when another opens. Two kinds: MCP Apps
// views (e.g. Judy) and embedded web apps from pre-configured iframe tasks (`kind: 'iframe'`, AT-2951).
const MAX_APP_VIEWS = 5
const appViewLabel = view => view.appName || view.serverLabel || view.toolName || 'App'

const NO_ASSISTANT = {}

function useMobilePane() {
    const [mobile, setMobile] = useState(() => window.matchMedia?.('(max-width: 760px)').matches || false)
    useEffect(() => {
        const media = window.matchMedia?.('(max-width: 760px)')
        if (!media) return
        const update = () => setMobile(media.matches)
        media.addEventListener('change', update)
        return () => media.removeEventListener('change', update)
    }, [])
    return mobile
}

// A route that never settles must not hold every later click (AT-2906).
const NAVIGATION_SETTLE_MS = 5000
const settleWithin = (promise, ms) =>
    new Promise(resolve => {
        const timer = setTimeout(resolve, ms)
        Promise.resolve(promise)
            .catch(() => {})
            .then(() => {
                clearTimeout(timer)
                resolve()
            })
    })

// Always mounted around the same route subtree, including in fullscreen Alldone. A layout switch
// must never destroy a note editor, an unsent message, a selection, or the workspace scroll state.
export default function AnnaShell({ children, routeId }) {
    useTranslator()
    const layoutMode = useAnnaLayout()
    const active = layoutMode !== 'alldone'
    const chatOnly = layoutMode === 'chat'
    const autoFollow = useRef(false)
    autoFollow.current = active && !chatOnly
    const user = useSelector(state => state.loggedUser)
    const openTasksAmount = useSelector(state =>
        getAllProjectsOpenTasksAmount(
            state.sidebarNumbers,
            state.loggedUser.uid,
            state.loggedUser.archivedProjectIds,
            state.loggedUser.templateProjectIds
        )
    )
    const call = useVoiceCall()
    const [visited, setVisited] = useState(active)
    const [textBusy, setTextBusy] = useState(false)
    const [workState, setWorkState] = useState(null)
    // AT-2903: the chat continues in the selected project, with that project's assistant.
    const projectSelection = useAnnaProjectSelection(user)
    const [projectNotice, setProjectNotice] = useState('')
    const onProjectUnavailable = useCallback(
        (projectId, message) => {
            projectSelection.select(null)
            setProjectNotice(
                message
                    ? `${message} ${translate('Continuing in your default project.')}`
                    : translate('The project chat could not be opened. Continuing in your default project.')
            )
        },
        [projectSelection.select]
    )
    const {
        conversation,
        loading,
        error,
        retry,
        threads,
        resolveConversation,
        loadEarlier,
        nextBefore,
        offline,
        activeProjectId,
        switching,
    } = useAnnaConversation(user.uid, {
        enabled: active,
        user,
        hold: textBusy || call.status !== 'idle',
        projectId: projectSelection.projectId,
        onProjectUnavailable,
    })
    useProjectData(activeProjectId, PROJECT_DATA_ASSISTANTS)
    const projectsMap = useSelector(state => state.loggedUserProjectsMap)
    const projectAssistants = useSelector(state => state.projectAssistants)
    const globalAssistants = useSelector(state => state.globalAssistants, shallowEqual)
    const defaultAssistant = useSelector(state => state.defaultAssistant) || NO_ASSISTANT
    const projectChoices = useMemo(
        () =>
            getAnnaProjectChoices({
                user,
                projectsMap: projectsMap || {},
                projectAssistants: projectAssistants || {},
                globalAssistants: globalAssistants || [],
                defaultAssistant,
            }),
        [
            user.uid,
            user.defaultProjectId,
            user.projectIds,
            user.archivedProjectIds,
            user.templateProjectIds,
            user.guideProjectIds,
            projectsMap,
            projectAssistants,
            globalAssistants,
            defaultAssistant,
        ]
    )
    const activeChoice = projectChoices.find(choice => choice.id === activeProjectId)
    const resolvedAssistant = useSelector(state =>
        conversation?.assistantId
            ? getAssistantFromState(state, conversation.assistantId) ||
              (state.defaultAssistant?.uid === conversation.assistantId ? state.defaultAssistant : null)
            : state.defaultAssistant
    )
    // While the selected project's chat resolves, preview the assistant that will answer there.
    const assistant = (switching ? activeChoice?.assistant : resolvedAssistant) || resolvedAssistant || NO_ASSISTANT
    const assistantName = assistant.displayName?.trim() || translate('Assistant')
    // The header names whoever answers next: the current default assistant in the
    // default project, the selected project's assistant everywhere else.
    const headerAssistant =
        !activeProjectId || activeProjectId === user.defaultProjectId
            ? defaultAssistant
            : (switching ? activeChoice?.assistant : resolvedAssistant) || activeChoice?.assistant || defaultAssistant
    const headerAssistantName = headerAssistant.displayName?.trim() || translate('Assistant')
    // AT-2914: a project picked in the switcher is shown in the chat history (locally only).
    const [projectSwitch, setProjectSwitch] = useState(null)
    const projectSwitchSequence = useRef(0)
    const selectProject = useCallback(
        projectId => {
            setProjectNotice('')
            const targetId = projectId || user.defaultProjectId
            const currentId = activeProjectId || conversation?.projectId || user.defaultProjectId
            if (targetId && targetId !== currentId) {
                projectSwitchSequence.current += 1
                setProjectSwitch({
                    id: projectSwitchSequence.current,
                    projectId: targetId,
                    name:
                        projectChoices.find(choice => choice.id === targetId)?.name ||
                        projectsMap?.[targetId]?.name ||
                        '',
                    created: Date.now(),
                })
            }
            projectSelection.select(projectId)
        },
        [
            projectSelection.select,
            user.defaultProjectId,
            activeProjectId,
            conversation?.projectId,
            projectChoices,
            projectsMap,
        ]
    )
    const [mobilePane, setMobilePane] = useState('chat')
    const [latestChatRequest, setLatestChatRequest] = useState(0)
    const [surface, setSurface] = useState('alldone')
    const surfaceRef = useRef(surface)
    surfaceRef.current = surface
    const selectedVmId = surface.startsWith('vm:') ? surface.slice(3) : null
    // MCP Apps views (e.g. Judy) the chat opened here, newest last; each stays mounted, so it keeps its state.
    const [appViews, setAppViews] = useState([])
    const selectedAppViewId = surface.startsWith('app:') ? surface.slice(4) : null
    const selectedAppView = appViews.find(view => view.viewId === selectedAppViewId) || null
    // The page each embedded web app last reported (EMBED_LOCATION, AT-2954), by view id.
    const appLocations = useRef({})
    // The app on screen in the pane, handed back to its embed window when assistant mode is left.
    const appOnExit = useRef(null)
    const vmSurface = surface === 'vms' || !!selectedVmId
    // An app view on the right is something the person is working in, like a VM: the assistant's own
    // navigation (a saved object's reveal, show_workspace, following its browser) must not replace it.
    const appSurface = !!selectedAppView
    const lastVmId = useRef(null)
    if (selectedVmId) lastVmId.current = selectedVmId
    // A card is a thread (AT-2916); remembering the run it showed keeps that run's result on screen
    // after it settles, even when its task is done and the thread therefore leaves the list.
    const selectedVmRun = useRef({ id: null, runId: null })
    const {
        jobs: vmJobs,
        error: vmError,
        retry: retryVms,
    } = useAnnaVmJobs(user.uid, {
        enabled: active,
        selectedId: selectedVmId,
        selectedRunId: selectedVmRun.current.id === selectedVmId ? selectedVmRun.current.runId : null,
    })
    const selectedVm = vmJobs.find(job => job.id === selectedVmId)
    if (selectedVm?.runId) selectedVmRun.current = { id: selectedVmId, runId: selectedVm.runId }
    // Latest pane inputs for the context publisher below (AT-2955).
    const paneInputs = useRef({})
    paneInputs.current = { selectedVm, selectedAppView, vmCount: vmJobs.length }
    const vmPointerDown = useRef(false)
    const [browserControl, setBrowserControl] = useState(false)
    const [browser, setBrowser] = useState(null)
    const browserCompleted = useAnnaBrowserCompletion(browser)
    const followedBrowser = useRef(null)
    const [browserTakeover, setBrowserTakeover] = useState(null)
    const takeoverRef = useRef(null)
    const [resumeRequest, setResumeRequest] = useState(null)
    const browserWorkspace = useRef(null)
    const [pending, setPending] = useState(null)
    const [workspacePage, setWorkspacePage] = useState(null)
    const [browserPage, setBrowserPage] = useState(null)
    const [navigationError, setNavigationError] = useState('')
    const workspaceContent = useRef(null)
    const layout = useRef(null)
    const chatPane = useRef(null)
    const chatConversation = useRef(null)
    const workspaceStage = useRef(null)
    const focusAfterSwitch = useRef(active ? 'chat' : null)
    const awaitingChatInputFocus = useRef(false)
    const previousMode = useRef(layoutMode)
    const { width: chatWidth, maximum: maxChatWidth, resize: setChatWidth } = useAnnaChatWidth(user.uid, layout)
    const presentationSeen = useRef(new Set())
    const browserSeen = useRef(null)
    const heldBrowser = useRef(false)
    const browserRef = useRef(null)
    const nextBrowserRef = useRef(null)
    const requestGeneration = useRef(0)
    const navigationTail = useRef(Promise.resolve())
    const revealGeneration = useRef(0)
    const mobile = useMobilePane()
    const visibleWorkspace = active && !chatOnly && (mobilePane === 'workspace' || !mobile)
    useWorkspaceViewportOwner(workspaceContent, active, visibleWorkspace && surface === 'alldone')
    const focusChatInput = useCallback(() => {
        chatPane.current?.focus({ preventScroll: true })
        awaitingChatInputFocus.current = !chatConversation.current
        chatConversation.current?.focusInput()
    }, [])
    const selectSurface = nextSurface => {
        if (mobile && nextSurface === 'alldone') {
            // Choosing Alldone over the app must not bring the app back as a window.
            appOnExit.current = null
            closeChat()
            return
        }
        requestGeneration.current++
        if (mobile) focusAfterSwitch.current = 'workspace'
        const vm = vmJobs.find(job => job.id === lastVmId.current) || vmJobs[0]
        setSurface(nextSurface === 'vms' && vm ? `vm:${vm.id}` : nextSurface)
        setAnnaLayout('split')
        setMobilePane('workspace')
    }
    const selectVm = runId => {
        if (runId === selectedVmId) return
        requestGeneration.current++
        setSurface(`vm:${runId}`)
    }
    // `auto`: opened because the answer arrived, not by a tap. On a phone that must not pull the user
    // out of the chat they are reading; the card under the answer opens the pane.
    const openAppView = useCallback(
        (view, { auto = false } = {}) => {
            if (!view?.viewId) return
            setAppViews(current => {
                if (current.some(item => item.viewId === view.viewId)) return current
                // One pane per app: a newer view of the same app (another judy_show) replaces the older.
                const key = mcpAppKey(view)
                return [...current.filter(item => mcpAppKey(item) !== key), view].slice(-MAX_APP_VIEWS)
            })
            requestGeneration.current++
            setSurface(`app:${view.viewId}`)
            if (auto && mobile) return
            if (mobile) focusAfterSwitch.current = 'workspace'
            setAnnaLayout('split')
            setMobilePane('workspace')
        },
        [mobile]
    )
    // The view's own "show in chat" button: close the pane and show Alldone again. The view stays
    // mounted, so its card reopens it as it was.
    const closeAppView = useCallback(viewId => {
        if (surfaceRef.current !== `app:${viewId}`) return
        requestGeneration.current++
        setSurface('alldone')
    }, [])
    const appPane = useMemo(
        () => ({ open: openAppView, selectedViewId: selectedAppViewId }),
        [openAppView, selectedAppViewId]
    )
    // The app-wide embed windows (IframeModal, McpAppModal) live outside this tree: they open their
    // app here through the bridge, both from their header's assistant-mode button and whenever an app
    // is opened while assistant mode is on (AT-2951).
    useLayoutEffect(() => registerAnnaAppPane(openAppView, active), [openAppView, active])
    // And back: leaving assistant mode (closing the chat, the top bar or sidebar toggle, browser Back)
    // while an app is the pane's selection opens that app in its embed window again, also from
    // chat-only mode where the pane is merely hidden (product decision). With Alldone selected, or an
    // app only listed in the picker, nothing comes back. The pane's copy is closed so the app never
    // runs twice. Runs after the registration above, so the windows already see assistant mode as
    // off and keep the app.
    const paneAppView = active ? selectedAppView : null
    useLayoutEffect(() => {
        if (active) {
            appOnExit.current = paneAppView
            return
        }
        const view = appOnExit.current
        appOnExit.current = null
        if (!view || !openInEmbedWindow(view)) return
        delete appLocations.current[view.viewId]
        setAppViews(current => current.filter(item => item.viewId !== view.viewId))
    }, [active, paneAppView])
    // The header button next to the X while an embedded web app is on screen (AT-2954): its current
    // page opens in a new browser tab and the pane's copy is closed, so the app never runs twice.
    // A blocked pop-up keeps the app where it is and says so.
    const openSelectedAppInNewTab = () => {
        const view = selectedAppView
        if (view?.kind !== 'iframe') return
        if (!openEmbeddedAppInNewTab(appLocations.current[view.viewId] || view.url)) {
            setNavigationError(translate('embedded_app_popup_blocked'))
            return
        }
        delete appLocations.current[view.viewId]
        setNavigationError('')
        requestGeneration.current++
        setSurface('alldone')
        setAppViews(current => current.filter(item => item.viewId !== view.viewId))
    }
    const closeChat = useCallback(() => {
        focusAfterSwitch.current = 'workspace'
        requestGeneration.current++
        followedBrowser.current = null
        setSurface('alldone')
        setMobilePane('chat')
        setAnnaMode(false)
    }, [])
    const dispatch = useDispatch()
    // The header's Home button always lands on the open task list, never on whatever
    // page the workspace happened to show last (AT-2920). The X keeps that page.
    const goHome = useCallback(() => {
        // Home means the task list, so an app open in the pane does not come back as a window.
        appOnExit.current = null
        closeChat()
        navigateToHome(dispatch)
    }, [closeChat, dispatch])
    const toggleWorkspace = () => {
        requestGeneration.current++
        followedBrowser.current = null
        setAnnaLayout(chatOnly ? 'split' : 'chat')
        setMobilePane(chatOnly ? 'workspace' : 'chat')
    }
    const backToChat = () => {
        focusAfterSwitch.current = 'chat'
        setMobilePane('chat')
    }
    const showLatestChat = () => {
        if (!mobile && visibleWorkspace) {
            requestGeneration.current++
            followedBrowser.current = null
            setSurface('alldone')
        }
        backToChat()
        setLatestChatRequest(request => request + 1)
    }
    useLayoutEffect(() => {
        if (!latestChatRequest) return
        // A hidden mobile pane has no scroll height. Reveal it before scrolling.
        // The input owns focus for this action; the normal pane-switch effect must not take it back.
        focusAfterSwitch.current = null
        chatPane.current?.focus({ preventScroll: true })
        chatConversation.current?.focusInput()
        chatConversation.current?.scrollToLatest()
    }, [latestChatRequest])
    const openChat = useCallback(() => {
        requestGeneration.current++
        // Reveal and mount the editor within the gesture, including on desktop.
        // Mobile browsers need this synchronous focus to open the keyboard.
        flushSync(() => {
            setVisited(true)
            setMobilePane('chat')
            setAnnaMode(true)
        })
        focusAfterSwitch.current = null
        focusChatInput()
        chatConversation.current?.scrollToLatest()
    }, [focusChatInput])
    const showAlldoneNavigation = useCallback(() => {
        if (!active) return
        requestGeneration.current++
        followedBrowser.current = null
        if (visibleWorkspace && surface === 'alldone') workspaceStage.current?.focus({ preventScroll: true })
        else focusAfterSwitch.current = 'workspace'
        setSurface('alldone')
        setMobilePane('workspace')
        setAnnaLayout('split')
    }, [active, visibleWorkspace, surface])
    useEffect(() => {
        const target = focusAfterSwitch.current || (previousMode.current === 'alldone' && active ? 'chat' : null)
        previousMode.current = layoutMode
        focusAfterSwitch.current = null
        if (target === 'chat' && active) focusChatInput()
        else if (target === 'workspace') {
            awaitingChatInputFocus.current = false
            workspaceStage.current?.focus({ preventScroll: true })
        }
    }, [layoutMode, mobilePane, surface, active, focusChatInput])
    useEffect(() => {
        if (!active) {
            awaitingChatInputFocus.current = false
            return
        }
        if (!awaitingChatInputFocus.current || !chatConversation.current) return
        awaitingChatInputFocus.current = false
        // Loading must not take focus back after the user starts editing elsewhere.
        if (chatPane.current?.contains(document.activeElement)) {
            chatConversation.current.focusInput()
        }
    }, [active, visited, conversation])
    useEffect(() => {
        if (!active) {
            requestGeneration.current++
            followedBrowser.current = null
            setSurface('alldone')
            setMobilePane('chat')
        }
    }, [active])
    useEffect(() => {
        if (surface === 'vms' && vmJobs.length) setSurface(`vm:${vmJobs[0].id}`)
    }, [surface, vmJobs])
    const surfaceLabel = selectedVmId
        ? selectedVm
            ? `VM · ${selectedVm.title}`
            : 'VM'
        : surface === 'vms'
          ? translate('VMs')
          : surface === 'browser'
            ? translate('Browser')
            : selectedAppView
              ? appViewLabel(selectedAppView)
              : 'Alldone'
    const returnFromBrowser = useCallback(runId => {
        const followed = followedBrowser.current
        if (
            !followed ||
            !autoFollow.current ||
            runId !== browserRef.current?.runId ||
            followed.key !== browserActivityKey(browserRef.current) ||
            followed.generation !== requestGeneration.current ||
            surfaceRef.current !== 'browser' ||
            heldBrowser.current ||
            takeoverRef.current ||
            document.hidden
        )
            return
        followedBrowser.current = null
        setSurface('alldone')
    }, [])
    useEffect(() => {
        if (!active || !browserCompleted) return
        const finish = () => returnFromBrowser(browser?.runId)
        finish()
        return subscribePageVisible(finish)
    }, [active, browserCompleted, browser?.runId, browserControl, browserTakeover, returnFromBrowser])

    const openBrowserTakeover = useCallback((approval, callbacks) => {
        if (takeoverRef.current && takeoverRef.current.approval.approvalId !== approval.approvalId) return
        requestGeneration.current++
        setAnnaMode(true)
        setSurface('browser')
        setMobilePane('workspace')
        if (!takeoverRef.current) {
            takeoverRef.current = { approval, ...callbacks }
            setBrowserTakeover(takeoverRef.current)
        }
    }, [])
    const closeBrowserTakeover = cancelled => {
        const current = takeoverRef.current
        takeoverRef.current = null
        setBrowserTakeover(null)
        if (cancelled) current?.onCancelled?.()
        else current?.onFinished?.()
    }

    useEffect(() => {
        if (active) setVisited(true)
    }, [active])
    useEffect(() => {
        if (!visited || !user.uid) return
        const stopBrowser = getDb()
            .doc(`users/${user.uid}/private/annaBrowser`)
            .onSnapshot(
                snapshot => {
                    const next = snapshot.exists ? snapshot.data() : null
                    nextBrowserRef.current = next
                    if (heldBrowser.current && browserRef.current?.runId !== next?.runId) return
                    browserRef.current = next
                    setBrowser(next)
                    const key = browserActivityKey(next)
                    if (key && browserSeen.current !== key) {
                        browserSeen.current = key
                        if (
                            autoFollow.current &&
                            surfaceRef.current !== 'vms' &&
                            !surfaceRef.current.startsWith('vm:') &&
                            !surfaceRef.current.startsWith('app:') &&
                            Date.now() - Number(next.updatedAt || 0) < 120000
                        ) {
                            followedBrowser.current = { key, generation: requestGeneration.current }
                            setSurface('browser')
                        }
                    }
                },
                () => {}
            )
        return () => {
            stopBrowser()
        }
    }, [visited, user.uid])

    const resumeWork = useCallback(
        (context, surfaceName = 'browser') => {
            if (!context) return
            // The ongoing voice conversation can continue using the released surface. Do not
            // enqueue a second text execution after that call has already handled the hand-back.
            if (call.status !== 'idle') return
            const daily =
                ['topics', 'chats'].includes(context.objectType) &&
                (context.objectId === `anna_${user.uid}` || context.objectId?.startsWith('AnnaChat'))
            const text = translate('I have returned browser control to you. Please continue my current request.')
            setResumeRequest({
                id: `${context.at}-${surfaceName}`,
                surface: surfaceName,
                text:
                    text +
                    (!daily && context.objectType === 'tasks' && !context.objectId?.startsWith('direct__')
                        ? `\nhttps://my.alldone.app/projects/${context.projectId}/tasks/${context.objectId}/chat`
                        : ''),
                thread: daily
                    ? { projectId: context.projectId, id: context.objectId, assistantId: context.assistantId }
                    : null,
            })
        },
        [user.uid, call.status]
    )
    const resumeForMessage = useCallback(async () => {
        setNavigationError('')
        // A new message or voice call returns the shared browser automatically.
        // That request is the continuation; do not post another chat message.
        setResumeRequest(null)
        // Chat remains usable, but only the login controller may hand back its session.
        if (takeoverRef.current) return
        if (heldBrowser.current && !browserWorkspace.current) throw new Error(translate('The browser is unavailable.'))
        await browserWorkspace.current?.releaseControl()
    }, [])
    const acknowledge = useCallback(
        (presentation, status) => {
            if (!conversation || (presentation.manual && conversation.annaPresentation?.id !== presentation.id)) return
            getDb()
                .doc(`chatObjects/${conversation.projectId}/chats/${conversation.id}`)
                .update({ annaPresentationStatus: { id: presentation.id, status, at: Date.now() } })
                .catch(() => {})
        },
        [conversation?.projectId, conversation?.id, conversation?.annaPresentation?.id]
    )
    const present = useCallback(
        async presentation => {
            const generation = ++requestGeneration.current
            setNavigationError('')
            if (presentation.view === 'anna') {
                setMobilePane('chat')
                acknowledge(presentation, 'presented')
                return
            }
            if (
                !(presentation.manual
                    ? isAnnaNavigationPath(presentation.path)
                    : isAnnaWorkspacePath(presentation.path))
            ) {
                setNavigationError(translate('This workspace link is not supported.'))
                acknowledge(presentation, 'failed')
                return
            }
            try {
                let path = presentation.path
                if (presentation.resolveTarget) {
                    // Activity rows check their target first, so a deleted object or a project the
                    // user cannot open is reported here instead of the router silently landing on
                    // "All projects" (AT-2906). A later click supersedes this one.
                    const resolved = await resolveAnnaActivityPath(path)
                    if (generation !== requestGeneration.current) return
                    if (resolved.error) {
                        setNavigationError(getAnnaTargetErrorMessage(resolved.error))
                        acknowledge(presentation, 'failed')
                        return
                    }
                    path = resolved.path
                }
                // The routes resolve their target asynchronously (they read the object first), so
                // two quick clicks could otherwise finish in the wrong order and leave the first
                // one open. Wait for the previous navigation (bounded), then only the latest runs.
                const previous = navigationTail.current
                let settle
                navigationTail.current = new Promise(resolve => {
                    settle = resolve
                })
                try {
                    await settleWithin(previous, NAVIGATION_SETTLE_MS)
                    if (generation !== requestGeneration.current) return
                    await URLTrigger.processUrl(NavigationService.createNavigationProp(), path)
                } finally {
                    settle()
                }
                if (generation !== requestGeneration.current) return
                setSurface('alldone')
                if (presentation.manual) {
                    setAnnaLayout('split')
                    setMobilePane('workspace')
                }
                setPending(null)
                acknowledge(presentation, !presentation.manual && !visibleWorkspace ? 'opened_hidden' : 'opened')
            } catch (_) {
                if (generation !== requestGeneration.current) return
                setNavigationError(translate('This item could not be opened. Please try again.'))
                acknowledge(presentation, 'failed')
            }
        },
        [acknowledge, visibleWorkspace]
    )

    useEffect(() => {
        const presentation = conversation?.annaPresentation
        if (!presentation?.id || presentationSeen.current.has(presentation.id)) return
        presentationSeen.current.add(presentation.id)
        const fresh = Date.now() - Number(presentation.createdAt || 0) < 120000
        if (!fresh && conversation?.annaPresentationStatus?.id === presentation.id) return
        if (!active || chatOnly || browserControl || browserTakeover || vmSurface || appSurface) {
            setPending(presentation)
            acknowledge(presentation, 'deferred')
        } else present(presentation)
    }, [
        conversation?.annaPresentation?.id,
        active,
        chatOnly,
        browserControl,
        browserTakeover,
        vmSurface,
        appSurface,
        present,
        acknowledge,
    ])

    useEffect(() => {
        if (!conversation) return
        let stopped = false,
            publishing = false,
            previous = ''
        const tick = async () => {
            if (stopped || publishing || document.hidden) return
            const page = sanitizeCallPageContext({ path: window.location.pathname, title: document.title })
            setWorkspacePage(previous =>
                previous?.path === page?.path && previous?.title === page?.title ? previous : page
            )
            // AT-2955: what the pane really shows (an app, a VM, the VM overview, the browser, or an
            // Alldone page), including what an embedded app reported about itself. Read through a ref
            // so a VM's status or an app's report reaches the next tick without restarting this loop.
            const inputs = paneInputs.current
            const context = buildAnnaPaneContext({
                active,
                visibleWorkspace,
                surface,
                page,
                selectedVm: inputs.selectedVm,
                selectedAppView: inputs.selectedAppView,
                appState: inputs.selectedAppView ? getAppModelContext(inputs.selectedAppView.viewId) : null,
                vmCount: inputs.vmCount,
            })
            setAnnaWorkspaceContext(context)
            const key = JSON.stringify(context)
            if (key === previous) return
            publishing = true
            try {
                await getDb()
                    .doc(`chatObjects/${conversation.projectId}/chats/${conversation.id}`)
                    .update({ annaPageContext: context })
                if (!stopped) previous = key
            } catch (_) {
                /* Retry fresh context only. */
            } finally {
                publishing = false
            }
        }
        tick()
        const timer = setInterval(tick, 1000)
        return () => {
            stopped = true
            clearInterval(timer)
            setAnnaWorkspaceContext(null)
        }
    }, [
        active,
        visibleWorkspace,
        surface,
        selectedVm?.id,
        selectedVm?.title,
        selectedAppView?.viewId,
        conversation?.projectId,
        conversation?.id,
    ])

    const openWorkspaceChange = useCallback(async (change, isCancelled) => {
        const generation = requestGeneration.current
        const assertAvailable = () => {
            if (
                !isAnnaWorkspacePath(change.path) ||
                heldBrowser.current ||
                takeoverRef.current ||
                surfaceRef.current.startsWith('vm:') ||
                surfaceRef.current.startsWith('app:') ||
                generation !== requestGeneration.current ||
                isCancelled()
            )
                throw new Error('Workspace is unavailable')
        }
        const waitForTarget = async ms => {
            const deadline = Date.now() + ms
            while (true) {
                assertAvailable()
                if (findWorkspaceObject(workspaceContent.current, change)) return true
                if (Date.now() >= deadline) return false
                await new Promise(resolve => setTimeout(resolve, 75))
            }
        }
        assertAvailable()
        revealGeneration.current = generation
        setSurface('alldone')
        // Keep the current view when its live listener can reveal the saved row.
        if (await waitForTarget(600)) return
        // Otherwise try its actual project/assignee list before opening details.
        // The list may need a fresh Firestore subscription before its row mounts.
        if (isAnnaWorkspacePath(change.listPath)) {
            try {
                await URLTrigger.processUrl(NavigationService.createNavigationProp(), change.listPath)
            } catch (_) {
                // Other saved changes may still use their verified detail link.
            }
            if (await waitForTarget(2500)) return
        }
        // A new task is confirmed in its list. Slow listeners or hidden rows
        // must not turn that confirmation into an unsolicited detail view.
        if (change.type === 'task' && change.change === 'created') return
        assertAvailable()
        await URLTrigger.processUrl(NavigationService.createNavigationProp(), change.path)
    }, [])

    const returnWorkspaceHome = useCallback(async () => {
        if (
            heldBrowser.current ||
            takeoverRef.current ||
            document.hidden ||
            revealGeneration.current !== requestGeneration.current
        )
            return
        await URLTrigger.processUrl(NavigationService.createNavigationProp(), '/projects/tasks/open')
    }, [])
    const currentWorkState = {
        busy:
            textBusy ||
            call.status !== 'idle' ||
            workState?.projectId !== conversation?.projectId ||
            workState?.chatId !== conversation?.id ||
            workState?.busy !== false,
        completedRequests: workState?.completedRequests || [],
    }
    // Lights the workspace frame while the assistant is changing the workspace from this
    // conversation (AT-2911), and only then: a tool call that creates or updates an Alldone
    // object, never a send in flight, thinking or a streamed answer (AT-2926).
    const assistantWorking =
        visibleWorkspace &&
        !mobile &&
        workState?.projectId === conversation?.projectId &&
        workState?.chatId === conversation?.id &&
        workState?.changingWorkspace === true

    const interceptLink = event => {
        if (
            !active ||
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        )
            return
        const anchor = event.target.closest?.('a[href]')
        const path = anchor && resolveAnnaLink(anchor.href, window.location.origin)
        if (!path) return
        event.preventDefault()
        event.stopPropagation()
        present({
            path,
            id: `link-${Date.now()}`,
            view: 'link',
            manual: true,
            resolveTarget: anchor.hasAttribute('data-anna-activity-link'),
        })
    }
    const photo = headerAssistant.photoURL50 || headerAssistant.photoURL300 || headerAssistant.photoURL
    const currentBrowserPage = browserPage?.runId === browser?.runId ? browserPage : browser
    const chatPageContext =
        !active || chatOnly
            ? null
            : surface === 'browser'
              ? {
                    surface,
                    path: currentBrowserPage?.url || '',
                    title: currentBrowserPage?.title || translate('Browser'),
                }
              : selectedVm || selectedAppView || vmSurface
                ? // AT-2955: name the app or VM on screen, never the hidden Alldone page behind it.
                  buildAnnaPaneContext({
                      active,
                      visibleWorkspace: true,
                      surface,
                      page: workspacePage,
                      selectedVm,
                      selectedAppView,
                      vmCount: vmJobs.length,
                  })
                : workspacePage && { ...workspacePage, surface }

    const pendingNotice = pending && (
        <div className="anna-pending" role="status">
            <span>
                {translate('%{assistantName} would like to show you:', { assistantName })} {pending.title}
            </span>
            <button onClick={() => present({ ...pending, manual: true })}>{translate('Open')}</button>
            <button
                aria-label={translate('Dismiss')}
                onClick={() => {
                    acknowledge(pending, 'dismissed')
                    setPending(null)
                }}
            >
                ×
            </button>
        </div>
    )

    const header = (
        <header className={`anna-header${!mobile && !chatOnly ? ' anna-header-sidebar' : ''}`}>
            <div className="anna-header-left">
                <button
                    type="button"
                    className="anna-header-assistant"
                    title={`${headerAssistantName} – ${translate('Latest messages')}`}
                    aria-label={`${headerAssistantName} – ${translate('Latest messages')}`}
                    onClick={showLatestChat}
                >
                    <span className="anna-small-avatar">
                        {photo ? <img src={photo} alt="" /> : headerAssistantName.charAt(0)}
                    </span>
                    <strong className="anna-brand">{headerAssistantName}</strong>
                </button>
                {!mobile && (
                    <button
                        type="button"
                        className="anna-pane-toggle anna-header-expand"
                        aria-label={translate(chatOnly ? 'Show workspace' : 'Hide workspace — chat only')}
                        title={translate(chatOnly ? 'Show workspace' : 'Hide workspace — chat only')}
                        aria-expanded={!visibleWorkspace}
                        onClick={toggleWorkspace}
                    >
                        <Icon name={chatOnly ? 'minimize-2' : 'maximize-2'} size={18} color="currentColor" />
                    </button>
                )}
            </div>
            {(mobile || chatOnly) && (
                <button
                    type="button"
                    className="anna-header-tasks"
                    aria-label={translate('%{count} open tasks today', { count: openTasksAmount })}
                    title={translate('%{count} open tasks today', { count: openTasksAmount })}
                    onClick={goHome}
                >
                    <span aria-hidden="true" className="anna-task-icon">
                        <Icon name="home" size={24} color="currentColor" />
                        <span className="anna-open-tasks-badge">
                            <AmountTag
                                feedAmount={openTasksAmount}
                                isFollowedButton={false}
                                showFullAmount={true}
                                style={{ backgroundColor: colors.Primary100 }}
                            />
                        </span>
                    </span>
                </button>
            )}
            <div className="anna-header-right">
                <label className="anna-header-picker">
                    <select
                        aria-label={translate('Workspace')}
                        value={mobile && !visibleWorkspace ? '' : vmSurface ? 'vms' : surface}
                        onChange={event => {
                            if (event.target.value === 'chat') backToChat()
                            else selectSurface(event.target.value)
                        }}
                    >
                        {mobile && !visibleWorkspace && (
                            // A mobile selection opens that pane, including when reopening the same surface.
                            <option value="" disabled hidden>
                                {vmSurface ? translate('VMs') : surfaceLabel}
                            </option>
                        )}
                        {mobile && visibleWorkspace && <option value="chat">{translate('Back to chat')}</option>}
                        <option value="alldone">Alldone</option>
                        <option value="browser">{translate('Browser')}</option>
                        <option value="vms">
                            {translate('VMs')}
                            {vmJobs.length ? ` (${vmJobs.length})` : ''}
                        </option>
                        {appViews.map(view => (
                            <option key={view.viewId} value={`app:${view.viewId}`}>
                                {appViewLabel(view)}
                            </option>
                        ))}
                    </select>
                    <span aria-hidden="true">
                        <Icon name="chevron-down" size={14} color="currentColor" />
                    </span>
                </label>
                {visibleWorkspace && selectedAppView?.kind === 'iframe' && (
                    <button
                        type="button"
                        className="anna-pane-toggle anna-header-open-tab"
                        aria-label={translate('Open app in new tab')}
                        title={translate('Open app in new tab')}
                        data-testid="anna-app-open-in-new-tab"
                        onClick={openSelectedAppInNewTab}
                    >
                        <Icon name="new-window" size={18} color="currentColor" />
                    </button>
                )}
                <button
                    type="button"
                    className="anna-pane-toggle anna-header-close"
                    aria-label={translate('Close assistant chat')}
                    title={translate('Close assistant chat')}
                    onClick={closeChat}
                >
                    <Icon name="x" size={20} color="currentColor" />
                </button>
            </div>
        </header>
    )

    const content = (
        <div
            className={`anna-shell anna-zoom-shell ${active ? (chatOnly ? 'anna-zoom-active anna-chat-only' : 'anna-zoom-active') : 'anna-fullscreen'} anna-mobile-${chatOnly ? 'chat' : mobilePane}`}
            style={{
                '--anna-chat-width': `${chatWidth}px`,
                '--anna-ink': colors.Text01,
                '--anna-muted': colors.Text02,
                '--anna-placeholder': colors.Text03,
                '--anna-accent': colors.Primary200,
                '--anna-unread': colors.UtilityRed200,
                '--anna-neutral': colors.Grey200,
                '--anna-tint': colors.UtilityBlue100,
                '--anna-hover': colors.UtilityBlue112,
                '--anna-border': colors.Grey300,
                '--anna-bezel': colors.Grey400,
                '--anna-focus': colors.UtilityBlue150,
                '--anna-surface': colors.Grey100,
            }}
            onClickCapture={interceptLink}
        >
            {mobile && active && header}
            <main className="anna-layout" ref={layout}>
                <section
                    className="anna-conversation"
                    ref={chatPane}
                    tabIndex={-1}
                    aria-label={translate('Conversation with %{assistantName}', { assistantName })}
                    aria-hidden={!active || (!chatOnly && mobile && mobilePane !== 'chat')}
                    inert={!active || (!chatOnly && mobile && mobilePane !== 'chat') ? '' : undefined}
                >
                    {!mobile && header}
                    {active && !visibleWorkspace && pendingNotice}
                    {active && !visibleWorkspace && navigationError && (
                        <div className="anna-error" role="alert">
                            {navigationError}
                        </div>
                    )}
                    {visited && (
                        <>
                            {loading && (
                                <div className="anna-empty" role="status">
                                    {translate('Loading conversation…')}
                                </div>
                            )}
                            {projectNotice && (
                                <div className="anna-error" role="alert">
                                    {projectNotice}
                                    <button onClick={() => setProjectNotice('')}>{translate('Dismiss')}</button>
                                </div>
                            )}
                            {error && (
                                <div className="anna-error" role="alert">
                                    {error}
                                    <button onClick={retry}>{translate('Try again')}</button>
                                </div>
                            )}
                            {offline && !conversation && (
                                <div className="anna-empty" role="status">
                                    {translate('This conversation is not available offline yet.')}
                                </div>
                            )}
                            {conversation && (
                                <AnnaAppPaneContext.Provider value={appPane}>
                                    <AnnaConversation
                                        ref={chatConversation}
                                        key={`${user.uid}/${user.defaultProjectId}`}
                                        conversation={conversation}
                                        assistant={assistant}
                                        user={user}
                                        call={call}
                                        threads={threads}
                                        resolveConversation={resolveConversation}
                                        loadEarlier={loadEarlier}
                                        hasEarlier={nextBefore != null}
                                        offline={offline}
                                        resumeRequest={browserControl ? null : resumeRequest}
                                        onResumeHandled={id =>
                                            setResumeRequest(current => (current?.id === id ? null : current))
                                        }
                                        visible={active && (chatOnly || !mobile || mobilePane === 'chat')}
                                        active={active}
                                        onExpand={() => setMobilePane('chat')}
                                        onSendingChange={setTextBusy}
                                        onWorkStateChange={setWorkState}
                                        onBeforeSend={resumeForMessage}
                                        pageContext={chatPageContext}
                                        switching={switching}
                                        projectSwitch={projectSwitch}
                                        renderProjectSwitcher={({ disabled }) => (
                                            <AnnaProjectSwitcher
                                                choices={projectChoices}
                                                activeProjectId={activeProjectId}
                                                onSelect={selectProject}
                                                disabled={disabled || call.status !== 'idle'}
                                                busy={switching}
                                                getViewport={() => {
                                                    const pane = chatPane.current?.getBoundingClientRect?.()
                                                    return pane && { ...pane.toJSON(), active: true }
                                                }}
                                            />
                                        )}
                                    />
                                </AnnaAppPaneContext.Provider>
                            )}
                        </>
                    )}
                </section>
                <div
                    className="anna-divider"
                    role="separator"
                    aria-label={translate('Conversation width')}
                    aria-orientation="vertical"
                    aria-valuemin={MIN_CHAT_WIDTH}
                    aria-valuemax={maxChatWidth}
                    aria-valuenow={chatWidth}
                    aria-valuetext={`${Math.round(chatWidth)}px`}
                    tabIndex={active && !chatOnly && !mobile ? 0 : -1}
                    onPointerDown={event => event.currentTarget.setPointerCapture(event.pointerId)}
                    onPointerMove={event => {
                        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                            const bounds = layout.current.getBoundingClientRect()
                            setChatWidth(event.clientX - bounds.left)
                        }
                    }}
                    onPointerUp={event => event.currentTarget.releasePointerCapture(event.pointerId)}
                    onKeyDown={event => {
                        if (['Home', 'End'].includes(event.key)) {
                            event.preventDefault()
                            setChatWidth(event.key === 'Home' ? MIN_CHAT_WIDTH : maxChatWidth)
                        }
                        if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
                            event.preventDefault()
                            setChatWidth(chatWidth + (event.key === 'ArrowLeft' ? -20 : 20))
                        }
                    }}
                />
                <section
                    className={`anna-stage ${assistantWorking ? 'anna-stage-working' : ''}`}
                    ref={workspaceStage}
                    tabIndex={-1}
                    aria-label={translate('Alldone workspace')}
                    aria-hidden={active && !visibleWorkspace}
                    inert={active && !visibleWorkspace ? '' : undefined}
                >
                    {assistantWorking && (
                        <div className="anna-working-label" role="status">
                            <span className="anna-working-dot" aria-hidden="true" />
                            {translate('%{assistantName} is working here', { assistantName })}
                        </div>
                    )}
                    {visibleWorkspace && pendingNotice}
                    {visibleWorkspace && navigationError && (
                        <div className="anna-error" role="alert">
                            {navigationError}
                        </div>
                    )}
                    <div
                        className={`anna-workspace ${active && surface !== 'alldone' ? 'anna-surface-hidden' : ''}`}
                        aria-hidden={active && surface !== 'alldone'}
                        inert={active && surface !== 'alldone' ? '' : undefined}
                    >
                        <div className="anna-workspace-content" ref={workspaceContent}>
                            {children}
                            {visited && (
                                <AnnaWorkspaceReveal
                                    rootRef={workspaceContent}
                                    conversation={conversation}
                                    assistantName={assistantName}
                                    available={
                                        visibleWorkspace &&
                                        !browserControl &&
                                        !browserTakeover &&
                                        !vmSurface &&
                                        !appSurface
                                    }
                                    onOpen={openWorkspaceChange}
                                    onComplete={returnWorkspaceHome}
                                    workState={currentWorkState}
                                />
                            )}
                            {visited && (
                                <AnnaWorkspaceHighlight
                                    assistantName={assistantName}
                                    rootRef={workspaceContent}
                                    active={visibleWorkspace && surface === 'alldone'}
                                    conversation={conversation}
                                    routeId={routeId}
                                />
                            )}
                        </div>
                    </div>
                    <div
                        className={`anna-vms ${!active || !vmSurface ? 'anna-surface-hidden' : ''}`}
                        aria-hidden={!active || !vmSurface}
                        inert={!active || !vmSurface ? '' : undefined}
                        onPointerDownCapture={() => {
                            vmPointerDown.current = true
                        }}
                        onPointerUpCapture={() => {
                            vmPointerDown.current = false
                        }}
                        onPointerCancelCapture={() => {
                            vmPointerDown.current = false
                        }}
                        onPointerLeave={() => {
                            vmPointerDown.current = false
                        }}
                    >
                        {vmError && (
                            <div className="anna-error" role="alert">
                                {translate('vm_workspace_unavailable')}
                                <button onClick={retryVms}>{translate('vm_workspace_retry')}</button>
                            </div>
                        )}
                        {vmJobs.map(job => (
                            <section
                                key={job.id}
                                className={`anna-vm-surface ${selectedVmId === job.id ? 'anna-vm-selected' : ''}`}
                                aria-label={job.title}
                                aria-current={selectedVmId === job.id ? 'true' : undefined}
                                tabIndex={0}
                                onFocusCapture={() => {
                                    // Selecting another run may remove a completed panel above it.
                                    // Keep pointer targets in place until their click has finished.
                                    if (!vmPointerDown.current) selectVm(job.id)
                                }}
                                onClickCapture={() => selectVm(job.id)}
                            >
                                <AnnaVmWorkspace job={job} active={visibleWorkspace && vmSurface} />
                            </section>
                        ))}
                        {vmSurface && (!vmJobs.length || (selectedVmId && !selectedVm)) && (
                            <div className={vmJobs.length ? 'anna-error' : 'anna-empty'} role="status">
                                {translate(selectedVmId ? 'vm_workspace_unavailable' : 'No VM sessions yet')}
                            </div>
                        )}
                    </div>
                    {appViews.length > 0 && (
                        <div
                            className={`anna-apps ${!active || !selectedAppView ? 'anna-surface-hidden' : ''}`}
                            aria-hidden={!active || !selectedAppView}
                            inert={!active || !selectedAppView ? '' : undefined}
                        >
                            {appViews.map(view => (
                                <div
                                    key={view.viewId}
                                    className={`anna-app-surface ${view.viewId === selectedAppViewId ? '' : 'anna-surface-hidden'}`}
                                >
                                    {view.kind === 'iframe' ? (
                                        <EmbeddedAppFrame
                                            url={view.url}
                                            contextId={view.viewId}
                                            title={appViewLabel(view)}
                                            onNavigateAway={() => closeAppView(view.viewId)}
                                            onLocationChange={location => {
                                                appLocations.current[view.viewId] = location
                                            }}
                                        />
                                    ) : (
                                        <McpAppFrame
                                            view={view}
                                            language={user.language}
                                            onClose={() => closeAppView(view.viewId)}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {visited && (
                        <div
                            className={`anna-browser-surface ${!active || surface !== 'browser' ? 'anna-surface-hidden' : ''}`}
                            aria-hidden={!active || surface !== 'browser'}
                            inert={!active || surface !== 'browser' ? '' : undefined}
                            onPointerDownCapture={() => {
                                followedBrowser.current = null
                            }}
                            onKeyDownCapture={() => {
                                followedBrowser.current = null
                            }}
                        >
                            {browserTakeover && (
                                <div className="anna-browser-takeover">
                                    <BrowserTakeoverPanel
                                        key={browserTakeover.approval.approvalId}
                                        approval={browserTakeover.approval}
                                        workspace
                                        onFinished={() => closeBrowserTakeover(false)}
                                        onCancelled={() => closeBrowserTakeover(true)}
                                    />
                                </div>
                            )}
                            <div hidden={!!browserTakeover} inert={browserTakeover ? '' : undefined}>
                                <AnnaBrowserWorkspace
                                    ref={browserWorkspace}
                                    onPageChange={setBrowserPage}
                                    assistantName={assistantName}
                                    browser={browser}
                                    active={visibleWorkspace && surface === 'browser' && !browserTakeover}
                                    onControlChange={held => {
                                        heldBrowser.current = held
                                        if (held) followedBrowser.current = null
                                        setBrowserControl(held)
                                        if (!held && nextBrowserRef.current?.runId !== browserRef.current?.runId) {
                                            browserRef.current = nextBrowserRef.current
                                            setBrowser(nextBrowserRef.current)
                                        }
                                    }}
                                    onResume={resumeWork}
                                    onSessionEnded={returnFromBrowser}
                                />
                            </div>
                        </div>
                    )}
                </section>
            </main>
        </div>
    )
    return (
        <AnnaBrowserTakeoverContext.Provider
            value={{ open: openBrowserTakeover, approvalId: browserTakeover?.approval.approvalId }}
        >
            <AnnaChatNavigationContext.Provider value={openChat}>
                <AnnaSidebarCloseContext.Provider value={active && !chatOnly && !mobile ? closeChat : null}>
                    <AnnaWorkspaceNavigationContext.Provider value={showAlldoneNavigation}>
                        {content}
                    </AnnaWorkspaceNavigationContext.Provider>
                </AnnaSidebarCloseContext.Provider>
            </AnnaChatNavigationContext.Provider>
        </AnnaBrowserTakeoverContext.Provider>
    )
}
